import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import InputCst from "@components/InputCst/InputCst";
import { LanguageSelectorNotificationProps } from "@components/InputCst/LanguageSelector/types";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import useSWRMutation from "swr/mutation";
import { MethodType } from "../../../../services/type";
import { fetcher } from "../../../../services/config";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const LanguageSelectorNotification: React.FC<
  LanguageSelectorNotificationProps
> = ({
  languages,
  id,
  idNotification,
  outputChannel,
  propertyName,
  label,
  disabled,
}) => {
  const { t } = useTranslation(["form"]);
  const [isLangPopupOpen, setLangPopupOpen] = useState(false);
  const [submitState, setSubmitState] = useState<{
    loading: boolean;
    submitted: boolean;
  }>({ loading: false, submitted: false });
  const [fieldStates, setFieldStates] = useState<Record<string, string>>({});

  const { dataDetail: translatedTemplates } = useFetchDataDetail(
    isLangPopupOpen && idNotification
      ? `${ServicesURL.getNotificationSecondaryTemplate.replace("idCorporate", id ? `${id}` : "1").replace("notificationId", `${idNotification}`)}?outputChannel=${outputChannel}`
      : null,
  );

  useMemo(() => {
    if (translatedTemplates?.templates) {
      const updatedFieldStates = Object.entries(
        translatedTemplates.templates,
      ).reduce(
        (acc: Record<string, string>, [lang, value]: [string, any]) => {
          acc[lang] = value?.[propertyName] ?? "";
          return acc;
        },
        {} as Record<string, string>,
      );

      setFieldStates(updatedFieldStates);
    }
  }, [translatedTemplates, propertyName]);

  const { trigger } = useSWRMutation(
    ServicesURL.putNotificationSecondaryTemplate.replace(
      "idCorporate",
      id ? `${id}` : "1",
    ),
    (url: string, arg: { arg: any }) =>
      fetcher(
        `${url.replace("notificationId", arg.arg.notificationId ? `${arg.arg.notificationId}` : "")}`,
        MethodType.PUT,
        arg.arg,
      ),
  );

  const handleLangIconClick = () => {
    setLangPopupOpen(true);
  };

  const handleLangPopupClose = () => {
    setLangPopupOpen(false);
  };

  const handleFieldChange = (langId: string, value: string) => {
    setFieldStates((prev) => ({
      ...prev,
      [langId]: value,
    }));

    setSubmitState({ loading: false, submitted: false });
  };

  const handleSubmit = async () => {
    const templates = Object.entries(fieldStates).reduce(
      (acc: Record<string, any>, [lang, value]) => {
        acc[lang] = { [propertyName]: value.trimStart().trimEnd() };
        return acc;
      },
      {} as Record<string, any>,
    );

    setSubmitState({ loading: true, submitted: false });

    try {
      await trigger({
        notificationId: idNotification,
        outputChannel,
        templates,
      });
      setSubmitState({ loading: false, submitted: true });
      handleLangPopupClose();
    } catch (error) {
      console.error("Error during submission:", error);
      setSubmitState({ loading: false, submitted: false });
    }
  };

  return (
    <>
      <IconButton onClick={handleLangIconClick} edge="end" disabled={disabled}>
        <LanguageIcon />
      </IconButton>
      <Dialog
        open={isLangPopupOpen}
        onClose={handleLangPopupClose}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "100%",
            "@media (min-width:600px)": {
              maxWidth: "80%",
            },
            "@media (min-width:960px)": {
              maxWidth: "70%",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
            paddingRight: "16px",
          }}
        >
          <Typography variant="h6" component="div">
            {label}
          </Typography>
          <IconButton onClick={handleLangPopupClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!translatedTemplates ? (
            <FormLoadingStateCst />
          ) : (
            <Grid
              container
              spacing={2}
              sx={{
                marginY: "20px",
              }}
            >
              {languages &&
                languages.secondary_languages.map((lang: any) => {
                  const fieldValue = fieldStates[lang.id] || "";

                  return (
                    <Grid
                      item
                      xs={12}
                      key={`${propertyName}-${lang.id}`}
                      sx={{
                        marginY: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <InputCst
                          id={`${propertyName}-${lang.id}`}
                          label={lang.label}
                          value={fieldValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleFieldChange(lang.id, value);
                          }}
                          multiline={propertyName === "description"}
                          rows={propertyName === "description" ? 4 : undefined}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {submitState.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("saveButton")
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageSelectorNotification;
